import React, { useEffect, useState } from "react";

import ChatIcon from "@mui/icons-material/Chat";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import CloseIcon from "@mui/icons-material/Close";
import SortIcon from "@mui/icons-material/Sort";
import FilterAlt from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import SignalWifiStatusbarConnectedNoInternet4Icon from "@mui/icons-material/SignalWifiStatusbarConnectedNoInternet4";
import NoteIcon from "@mui/icons-material/EditNote";

import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Collapse from "@mui/material/Collapse";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Popover from "@mui/material/Popover";

import { useUserAuth } from "../contexts/authContext";
import { useEnv } from "../contexts/envContext";
import { useMap } from "../contexts/mapContext";
import { useChat } from "../contexts/chatContext";

import { getOperationEditable, getOperationPriority, canUserEditOrDelete, handleFlightStatusChange, getFlightTransitions } from "../manager/operations/opUtil";
import { ConvertMetersToFeet, GetColorFromState } from "../util";
import { getFlightCardsSortTooltipFromSortValue } from "./mapUtil";

import * as CONSTANT from "../manager/operations/opConstants";

export const MapFlightCards = (props) => {
    const { user, organizations, userOperationalStates, handleFailedFetch, userProfileSettings, updateProfileSettings } = useUserAuth();
    const { conversations, createConversation, setSelectedConversationUUID } = useChat();
    const { drones } = useMap();
    const { laancOn } = useEnv();

    const [operationDetailsVisible, setOperationDetailsVisible] = useState(new Map());
    const [operations, setOperations] = useState([]);

    const [filters, setFilters] = useState(userProfileSettings.sidebar.flight_cards.filters);
    const [filterListAnchorEl, setFilterListAnchorEl] = useState(null);
    const filterListOpen = Boolean(filterListAnchorEl);

    const [sortBy, setSortBy] = useState(userProfileSettings.sidebar.flight_cards.sort);
    const [sortListAnchorEl, setSortListAnchorEl] = useState(null);
    const sortListOpen = Boolean(sortListAnchorEl);

    useEffect(() => {
        setSelectedConversationUUID(null);
    }, []);

    useEffect(() => {
        const currDate = new Date();
        currDate.setHours(0, 0, 0, 0);

        const filteredOperations = props.operations.filter((operation) => {
            let todayCritera = true;
            if (filters.includes("TODAYS_FLIGHTS")) {
                const startDate = new Date(operation.time_start);
                startDate.setHours(0, 0, 0, 0);

                const endDate = new Date(operation.time_end);
                endDate.setHours(0, 0, 0, 0);
                todayCritera = currDate.getTime() >= startDate.getTime() && currDate.getTime() <= endDate.getTime();
            }
            let activeCriteria = true;
            if (filters.includes("ACTIVE_FLIGHTS")) {
                activeCriteria = operation.state === "ACTIVATED" || operation.state === "CONTINGENT" || operation.state === "NONCONFORMING";
            }
            return todayCritera && activeCriteria;
        });
        const sortedOperations = filteredOperations.sort((a, b) => {
            switch (sortBy) {
                case "TIME_START_ASC":
                    return new Date(a.time_start).getTime() - new Date(b.time_start).getTime();
                case "TIME_START_DESC":
                    return new Date(b.time_start).getTime() - new Date(a.time_start).getTime();
                case "TIME_END_ASC":
                    return new Date(a.time_end).getTime() - new Date(b.time_end).getTime();
                case "TIME_END_DESC":
                    return new Date(b.time_end).getTime() - new Date(a.time_end).getTime();
                case "NAME_A_Z":
                    return a.name.localeCompare(b.name);
                case "NAME_Z_A":
                    return b.name.localeCompare(a.name);
            }
        });
        setOperations(sortedOperations);

        const opDetails = new Map();
        sortedOperations.forEach((op) => {
            const value = operationDetailsVisible.get(op.flight_uuid) || false;
            opDetails.set(op.flight_uuid, value);
        });
        setOperationDetailsVisible(opDetails);
    }, [props.operations, filters, sortBy]);

    const handleWatchButtonClicked = (operation) => {
        const entityId = operation.otd_uuid;
        props.handleWatchEntity(entityId);
    };

    const handleNotesButtonClicked = (flight) => {
        props.setNotesFlight(flight);
        props.setNotesOpen(true);
    };

    const handleChatButtonClicked = (id, email) => {
        const conversation = conversations.find(({ recipients }) => recipients.includes(id) && recipients.includes(user.id) && recipients.length === 2);
        if (conversation) {
            setSelectedConversationUUID(conversation.conversation_uuid);
        } else {
            createConversation([id, user.id], [email, user.email]);
        }
        props.setConversationDialogOpen(true);
    };

    const handleChangeOperationStatus = async (e, op) => {
        // wait for all state updates before proceeding, required to ensure the select menu closes when in fullscreen mode
        await new Promise((resolve) => setTimeout(() => resolve(), 0));
        handleFlightStatusChange(op, e.target.value, user, handleFailedFetch);
    };

    const handleEditButtonClicked = (operation) => {
        props.setEditFlightDialogOpen(true);
        props.setEditFlightVolume(operation);
    };

    const handleDetailsButtonClicked = (operation) => {
        props.setFlightDetailsDialogOpen(true);
        props.setFlightDetailsVolume(operation);
    };

    const handleToggleOperationDetails = ({ flight_uuid: flightUUID }) => {
        const newOperationDetailsVisible = new Map();
        operationDetailsVisible.forEach((value, key) => {
            if (key === flightUUID) {
                newOperationDetailsVisible.set(key, !value);
            } else newOperationDetailsVisible.set(key, false);
        });
        setOperationDetailsVisible(newOperationDetailsVisible);
    };

    const handleFilterOperationsButtonClick = (filter) => {
        setFilters((prev) => {
            let newFilters = [];
            if (prev.includes(filter)) {
                newFilters = prev.filter((value) => {
                    return value !== filter;
                });
            } else {
                newFilters = [...prev, filter];
            }
            updateFlightCardSettings(newFilters, sortBy);
            return newFilters;
        });
    };

    const handleSortOperationsButtonClick = (sortValue) => {
        setSortBy(sortValue);
        updateFlightCardSettings(filters, sortValue);
    };

    const updateFlightCardSettings = (filters, sortBy) => {
        const updatedProfileSettings = {
            ...userProfileSettings,
            sidebar: {
                ...userProfileSettings.sidebar,
                flight_cards: {
                    filters: filters,
                    sort: sortBy
                }
            }
        };
        updateProfileSettings(updatedProfileSettings);
    };

    return (
        <Box sx={{ position: "relative", width: "100%" }}>
            <Paper sx={{ display: "flex", flexDirection: "row", alignItems: "center", p: 2, borderBottom: "2px solid white" }}>
                <Typography variant="h6" noWrap>
                    Flights
                </Typography>

                {props.mapSidebarPanelIndex === 0 ? (
                    <Tooltip title={"View tagged vehicles"}>
                        <IconButton size="small" onClick={() => props.handleChangeMapSidebarPanel(1)} sx={{ mr: "auto" }}>
                            <ChevronRightIcon />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <></>
                )}
                <Tooltip title={`${filters.length} filter${filters.length !== 1 ? "s" : ""} active`}>
                    <IconButton size="small" onClick={(e) => setFilterListAnchorEl(e.currentTarget)}>
                        {filters.length > 0 ? <FilterAlt /> : <FilterAltOffIcon />}
                    </IconButton>
                </Tooltip>

                <Popover
                    open={filterListOpen}
                    anchorEl={filterListAnchorEl}
                    onClose={() => setFilterListAnchorEl(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    transformOrigin={{ vertical: "top", horizontal: "center" }}
                    slotProps={{ paper: { sx: { px: 2, py: 1 } } }}
                    container={props.mapContainer}
                >
                    <FormGroup>
                        {[
                            { label: "Today's Flights", value: "TODAYS_FLIGHTS" },
                            { label: "Active Flights", value: "ACTIVE_FLIGHTS" }
                        ].map(({ label, value }, filterIdx) => (
                            <FormControlLabel
                                key={filterIdx}
                                label={label}
                                control={<Checkbox checked={filters.includes(value)} onChange={() => handleFilterOperationsButtonClick(value)} />}
                            />
                        ))}
                    </FormGroup>
                </Popover>

                <Tooltip title={getFlightCardsSortTooltipFromSortValue(sortBy)}>
                    <IconButton size="small" onClick={(e) => setSortListAnchorEl(e.currentTarget)}>
                        <SortIcon />
                    </IconButton>
                </Tooltip>

                <Popover
                    open={sortListOpen}
                    anchorEl={sortListAnchorEl}
                    onClose={() => setSortListAnchorEl(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    transformOrigin={{ vertical: "top", horizontal: "center" }}
                    slotProps={{ paper: { sx: { px: 2, py: 1 } } }}
                    container={props.mapContainer}
                >
                    <FormGroup>
                        {[
                            { label: "Start time (asc)", value: "TIME_START_ASC" },
                            { label: "Start time (desc)", value: "TIME_START_DESC" },
                            { label: "End time (asc)", value: "TIME_END_ASC" },
                            { label: "End time (desc)", value: "TIME_END_DESC" },
                            { label: "Name A-Z", value: "NAME_A_Z" },
                            { label: "Name Z-A", value: "NAME_Z_A" }
                        ].map(({ label, value }, sortIdx) => (
                            <FormControlLabel
                                key={sortIdx}
                                label={label}
                                control={<Checkbox checked={sortBy === value} onChange={() => handleSortOperationsButtonClick(value)} />}
                            />
                        ))}
                    </FormGroup>
                </Popover>

                <Tooltip title={"Close"}>
                    <IconButton size="small" onClick={() => props.setMapSidebarOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </Tooltip>
            </Paper>

            {Array.isArray(operations) && operations.length > 0 ? (
                operations.map((operation) => {
                    const expanded = operationDetailsVisible.get(operation.flight_uuid);
                    const options = { year: "numeric", month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" };
                    const isOwnOp = user.id === operation.created_user_id ? true : false;

                    const editable = getOperationEditable(operation, user);
                    const canChangeStatus = canUserEditOrDelete(operation, user);
                    const priority = getOperationPriority(operation);
                    const minAlt = ConvertMetersToFeet(operation.volumes[0].altitude_min_agl_m);
                    const maxAlt = ConvertMetersToFeet(operation.volumes[0].altitude_max_agl_m);
                    const org = organizations?.filter((org) => org.id === operation.organization_id)[0];
                    const orgName = org ? org.name : "Unknown";

                    const faaRequired = operation.faa_approval_required ? true : false;
                    const laancState = Object.prototype.hasOwnProperty.call(operation, "laanc") ? operation.laanc.state.replace(/_/g, " ") : "NOT REQUIRED";

                    const entityVisible = props.handleCheckFlightASDVisible(operation.otd_uuid);
                    const vehicle = drones.find((drone) => drone.vehicle_uuid === operation.vehicle_uuid);
                    const start = new Date(operation.time_start).toLocaleString("en-US", options);
                    const end = new Date(operation.time_end).toLocaleString("en-US", options);

                    let alertVolumeAlerts = false;
                    props.activeAlertVolumeConflictionAlerts.forEach((alert) => {
                        if (alert.flight_uuid === operation.flight_uuid) {
                            alertVolumeAlerts = true;
                        }
                    });
                    let zcAlerts = [];
                    if (props.activeZeroConflictionAlerts.has(operation.otd_uuid)) {
                        zcAlerts = props.activeZeroConflictionAlerts.get(operation.otd_uuid);
                    }
                    const zcAlertsContainWarning = zcAlerts.find(({ severity }) => {
                        return severity === "WARNING";
                    });
                    const pilot =
                        operation.pilot_name && operation.pilot_name.trim() ? operation.pilot_name : operation.pilot_email ? operation.pilot_email : "Unknown";

                    const canChat =
                        operation.created_user_id !== user.id &&
                        (operation.organization_id === user.organization_id || user.user_role_id === 4) &&
                        pilot !== "Unknown";

                    const laancButtonInvisible =
                        operation.laanc &&
                        operation.laanc.state !== CONSTANT.LAANC_RESCINDED_AWAITING &&
                        operation.laanc.state !== CONSTANT.LAANC_INVALID_AWAITING
                            ? true
                            : false;

                    return (
                        <Card key={operation.flight_uuid} sx={{ m: "4px", overflow: "unset" }}>
                            <CardHeader
                                title={
                                    <Box sx={{ display: "flex", flexDirection: "row" }}>
                                        <Typography
                                            variant="h5"
                                            sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "250px", mr: "auto" }}
                                        >
                                            <Tooltip title={operation.name}>
                                                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whitespace: "nowrap" }}>{operation.name}</span>
                                            </Tooltip>
                                        </Typography>
                                        {entityVisible && (
                                            <Tooltip title="Monitor">
                                                <IconButton aria-label="settings" onClick={() => handleWatchButtonClicked(operation)}>
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="View">
                                            <IconButton aria-label="View" onClick={() => props.handleJumpToOperation(operation.flight_uuid)}>
                                                <TravelExploreIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Notes">
                                            <IconButton aria-label="Notes" onClick={() => handleNotesButtonClicked(operation)}>
                                                <NoteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                }
                                subheader={`${start} - ${end}`}
                                subheaderTypographyProps={{ noWrap: true }}
                                sx={{ pb: 1, borderBottom: 1 }}
                            />

                            <CardContent sx={{ py: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ display: "flex", justifyContent: "space-between", gap: 1, overflow: "hidden", whiteSpace: "nowrap" }}
                                >
                                    <b>Vehicle: </b>
                                    <Tooltip title={vehicle ? vehicle.name : "Unknown"}>
                                        <span
                                            style={{
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whitespace: "nowrap"
                                            }}
                                            id={`vehicle-${operation.volumeId}`}
                                        >
                                            {vehicle ? vehicle.name : "Unknown"}
                                        </span>
                                    </Tooltip>
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ display: "flex", justifyContent: "space-between", gap: 1, overflow: "hidden", whiteSpace: "nowrap" }}
                                >
                                    <b>Operator:</b>
                                    <Tooltip title={pilot}>
                                        <span
                                            style={{
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whitespace: "nowrap"
                                            }}
                                            id={`operator-${operation.volumeId}`}
                                        >
                                            {canChat ? (
                                                <IconButton
                                                    sx={{ fontSize: "small", height: "20px", width: "20px", overflowY: "visible" }}
                                                    onClick={() => handleChatButtonClicked(operation.created_user_id, operation.pilot_email)}
                                                >
                                                    <ChatIcon fontSize="inherit" />
                                                </IconButton>
                                            ) : (
                                                <></>
                                            )}
                                            {pilot}
                                        </span>
                                    </Tooltip>
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ display: "flex", justifyContent: "space-between", gap: 1, overflow: "hidden", whiteSpace: "nowrap" }}
                                >
                                    <b>Airspace Status: </b>
                                    {operation.conflicted ? (
                                        <Tooltip title="CONFLICTED">
                                            <span
                                                style={{ color: "#CB4C4E", overflow: "hidden", textOverflow: "ellipsis", whitespace: "nowrap" }}
                                                id={`airspaceStatus-${operation.volumeId}`}
                                            >
                                                CONFLICTED
                                            </span>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip title="CLEAR">
                                            <span
                                                style={{ color: "#5fa052", overflow: "hidden", textOverflow: "ellipsis", whitespace: "nowrap" }}
                                                id={`airspaceStatus-${operation.volumeId}`}
                                            >
                                                CLEAR
                                            </span>
                                        </Tooltip>
                                    )}
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ display: "flex", justifyContent: "space-between", gap: 1, overflow: "hidden", whiteSpace: "nowrap" }}
                                >
                                    <b>Alerts: </b>
                                    <span
                                        style={{
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whitespace: "nowrap"
                                        }}
                                    >
                                        <span style={{ color: zcAlertsContainWarning ? "red" : "yellow" }}>
                                            {zcAlerts.length > 0
                                                ? alertVolumeAlerts
                                                    ? `${zcAlerts.length} DAA Alert${zcAlerts.length !== 1 ? "s, " : ", "}`
                                                    : `${zcAlerts.length} DAA Alert${zcAlerts.length !== 1 ? "s" : ""}`
                                                : ""}
                                        </span>
                                        <span style={{ color: "yellow" }}>{alertVolumeAlerts ? "In Alert Volume" : ""}</span>
                                    </span>
                                </Typography>
                            </CardContent>

                            <CardActions>
                                <FormControl size="small" sx={{ minWidth: "125px" }} disabled={!canChangeStatus}>
                                    <InputLabel id="select-label-status">Op Status</InputLabel>
                                    <Select
                                        label="Op Status"
                                        labelId="select-label-status"
                                        value={operation.state}
                                        onChange={(e) => handleChangeOperationStatus(e, operation)}
                                        sx={{ color: GetColorFromState(operation.state, userOperationalStates), maxHeight: "36px" }}
                                        inputProps={{ "data-testid": "selectStatus" }}
                                        MenuProps={{ container: props.mapContainer }}
                                        id={`state-${operation.volumeId}`}
                                    >
                                        {getFlightTransitions(operation).map((status) => (
                                            <MenuItem key={status} value={status}>
                                                {status}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {editable ? (
                                    <Button
                                        variant="outlined"
                                        onClick={() => handleEditButtonClicked(operation)}
                                        data-testid="edit"
                                        id={`edit-${operation.volumeId}`}
                                    >
                                        Edit
                                    </Button>
                                ) : (
                                    <></>
                                )}
                                {laancOn === "true" && isOwnOp && faaRequired ? (
                                    <Badge badgeContent={"!"} color={"error"} invisible={laancButtonInvisible}>
                                        <Button
                                            variant="outlined"
                                            color={laancButtonInvisible ? "primary" : "error"}
                                            onClick={() => handleDetailsButtonClicked(operation)}
                                            data-testid="laanc"
                                        >
                                            LAANC
                                        </Button>
                                    </Badge>
                                ) : (
                                    <></>
                                )}
                                <IconButton onClick={() => handleToggleOperationDetails(operation)} sx={{ ml: "auto !important" }}>
                                    {expanded ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                            </CardActions>

                            <Collapse in={expanded} timeout="auto" unmountOnExit>
                                <CardContent sx={{ borderTop: 1, display: "grid", gridTemplateColumns: "1fr", gap: 1 }}>
                                    {[
                                        {
                                            title: "Op UUID",
                                            value: operation.flight_uuid,
                                            visible: true
                                        },
                                        {
                                            title: "Priority",
                                            value: priority,
                                            visible: true
                                        },
                                        {
                                            title: "Version",
                                            value: operation.version,
                                            visible: true
                                        },
                                        {
                                            title: "Min Altitude (AGL)",
                                            value: `${minAlt}ft`,
                                            visible: true
                                        },
                                        {
                                            title: "Max Altitude (AGL)",
                                            value: `${maxAlt}ft`,
                                            visible: true
                                        },
                                        {
                                            title: "Organization",
                                            value: orgName,
                                            visible: true
                                        },
                                        {
                                            title: "LAANC State",
                                            value: laancState,
                                            visible: laancOn === "true"
                                        }
                                    ].map(({ title, value, visible }, i) =>
                                        visible ? (
                                            <Box
                                                key={i}
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    width: "100%"
                                                }}
                                            >
                                                <Typography variant="body2" color="text.secondary" fontWeight="bold">
                                                    {title}:
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {value}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <React.Fragment key={i} />
                                        )
                                    )}
                                </CardContent>
                            </Collapse>
                        </Card>
                    );
                })
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, mt: 15 }}>
                    <SignalWifiStatusbarConnectedNoInternet4Icon sx={{ fontSize: "7rem" }} />
                    <Typography variant={"body1"}>No flights found</Typography>
                </Box>
            )}
        </Box>
    );
};
